import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPhaseConfig } from './PhaseChip';

/* ─── Confidence gauge ──────────────────────────────────────────────── */
function ConfidenceCell({ value, confidenceLevel }) {
  const { t } = useTranslation();
  const pct = Math.round((value || 0) * 100);
  const colorByLevel = {
    high: '#10b981',
    fair: '#8bc34a',
    needsConfirmation: '#f59e0b',
    weak: '#94a3b8',
  };
  const labelKey = confidenceLevel || 'weak';
  const color = colorByLevel[labelKey] || colorByLevel.weak;
  const label = t(`phase.table.confidenceLevels.${labelKey}`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 150 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={{
            height: 6,
            borderRadius: 999,
            backgroundColor: '#f1f5f9',
            overflow: 'hidden',
          }}>
            <Box sx={{
              width: `${pct}%`,
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${color}cc, ${color})`,
              transition: 'width 0.4s ease',
            }} />
          </Box>
        </Box>
        <Typography sx={{
          fontSize: '0.8rem',
          fontWeight: 700,
          fontFamily: 'monospace',
          color,
          minWidth: 34,
          textAlign: 'right',
        }}>
          {pct}%
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color, lineHeight: 1.1, letterSpacing: '0.01em' }}>
        {label}
      </Typography>
    </Box>
  );
}

function FlatPhaseLabel({ phase }) {
  const { t } = useTranslation();
  const config = getPhaseConfig(phase);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}>
      <Box sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: config.color,
        boxShadow: `0 0 0 3px ${config.bgColor}`,
      }} />
      <Typography sx={{
        fontSize: '0.78rem',
        fontWeight: 700,
        color: '#0f172a',
        letterSpacing: '0.01em',
        lineHeight: 1.1,
      }}>
        {t(`phase.phases.${phase}`)}
      </Typography>
    </Box>
  );
}

function SignalReadout({ summary }) {
  const { t } = useTranslation();
  const toneColors = {
    positive: '#059669',
    info: '#2563eb',
    negative: '#dc2626',
    warning: '#b45309',
    accent: '#7c3aed',
    neutral: '#94a3b8',
  };

  if (!summary?.primary_key) {
    return (
      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.35 }}>
        {t('phase.table.signalSummary.neutral')}
      </Typography>
    );
  }

  const primaryColor = toneColors[summary.tone] || toneColors.neutral;
  const primaryText = t(`phase.table.signalSummary.${summary.primary_key}`);
  const secondaryText = summary.secondary_key
    ? t(`phase.table.signalSummary.${summary.secondary_key}`)
    : null;

  return (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: primaryColor, lineHeight: 1.2 }}>
        {primaryText}
      </Typography>
      {secondaryText ? (
        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.15, mt: 0.15 }}>
          {secondaryText}
          {summary.extra_count > 0 ? t('phase.table.signalSummary.more', { count: summary.extra_count }) : ''}
        </Typography>
      ) : null}
    </Box>
  );
}

function DateCell({ tradeDate, freshness }) {
  const { t } = useTranslation();
  const freshnessText = !freshness || freshness.label_key === 'unknown'
    ? '—'
    : t(`phase.table.freshness.${freshness.label_key}`, { count: freshness.days_ago ?? 0 });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
      <Typography sx={{
        fontSize: '0.72rem',
        color: '#64748b',
        fontFamily: 'monospace',
        lineHeight: 1.1,
      }}>
        {tradeDate}
      </Typography>
      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.1 }}>
        {freshnessText}
      </Typography>
    </Box>
  );
}

/* ─── Mini score bars ───────────────────────────────────────────────── */
const SCORE_ITEMS = [
  { key: 'accumulation', color: '#3b82f6' },
  { key: 'markup',       color: '#10b981' },
  { key: 'distribution', color: '#ef4444' },
  { key: 'absorption',   color: '#f59e0b' },
];

function ScoreBars({ scores, phase }) {
  const { t } = useTranslation();
  if (!scores) return null;

  // Find the winner
  const vals = SCORE_ITEMS.map(item => ({ ...item, val: scores[item.key] || 0 }));
  const maxVal = Math.max(...vals.map(v => v.val), 0.01);
  const winner = vals.reduce((a, b) => b.val > a.val ? b : a, vals[0]);

  // Full tooltip
  const tooltipContent = (
    <Box sx={{ p: 0.3 }}>
      {vals.map(v => (
        <Box key={v.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            backgroundColor: v.color,
            opacity: v.key === winner.key ? 1 : 0.5,
          }} />
          <Typography sx={{
            fontSize: 11,
            fontWeight: v.key === winner.key ? 700 : 400,
            color: v.key === winner.key ? '#fff' : 'rgba(255,255,255,0.7)',
            minWidth: 70,
          }}>
            {t(`phase.phases.${v.key}`)}
          </Typography>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
            color: v.key === winner.key ? '#fff' : 'rgba(255,255,255,0.7)',
          }}>
            {Math.round(v.val * 100)}%
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top" enterDelay={200}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.9,
        cursor: 'default', minWidth: 112,
      }}>
        <Typography sx={{
          fontSize: '0.82rem',
          fontWeight: 800,
          fontFamily: 'monospace',
          color: winner.color,
          lineHeight: 1,
          minWidth: 28,
          textAlign: 'right',
        }}>
          {Math.round(winner.val * 100)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 22 }}>
          {vals.map(v => {
            const h = Math.max((v.val / maxVal) * 20, 3);
            const isWin = v.key === winner.key;
            return (
              <Box key={v.key} sx={{
                width: 6,
                height: h,
                borderRadius: '2px',
                backgroundColor: isWin ? v.color : `${v.color}30`,
                transition: 'height 0.3s ease',
              }} />
            );
          })}
        </Box>
      </Box>
    </Tooltip>
  );
}

function FeatureStrip({ features }) {
  if (!features) return null;

  const items = [];

  if (Number.isFinite(features.atr_contract)) {
    items.push(`ATR ${features.atr_contract.toFixed(2)}x`);
  }
  if (Number.isFinite(features.vol_ratio)) {
    items.push(`Vol ${features.vol_ratio.toFixed(2)}x`);
  }
  if (Number.isFinite(features.close_pos)) {
    items.push(`Close ${Math.round(features.close_pos * 100)}%`);
  }

  if (!items.length) return null;

  return (
    <Typography sx={{ mt: 0.3, fontSize: '0.64rem', color: '#64748b', lineHeight: 1.25 }}>
      {items.slice(0, 3).join(' · ')}
    </Typography>
  );
}

/* ─── Rank badge ────────────────────────────────────────────────────── */
function RankBadge({ rank }) {
  const colors = rank <= 3
    ? { bg: '#eef2ff', text: '#5b6ee1', border: '1px solid #dbe4ff', shadow: 'none' }
    : rank <= 10
      ? { bg: '#ffffff', text: '#64748b', border: '1px solid #e2e8f0', shadow: 'none' }
      : { bg: '#ffffff', text: '#94a3b8', border: '1px solid #edf2f7', shadow: 'none' };

  return (
    <Box sx={{
      width: 28,
      height: 28,
      borderRadius: '8px',
      background: colors.bg,
      border: colors.border,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: colors.shadow,
    }}>
      <Typography sx={{
        fontSize: '0.68rem',
        fontWeight: 800,
        color: colors.text,
        fontFamily: 'monospace',
      }}>
        {rank}
      </Typography>
    </Box>
  );
}

/* ─── Main table ────────────────────────────────────────────────────── */
export default function PhaseStockTable({
  stocks,
  showScores = true,
  showCompany = true,
  showSector = true,
  emptyLabel = 'phase.table.empty',
  onRowClick,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!stocks || stocks.length === 0) {
    return (
      <Box sx={{
        p: 6,
        textAlign: 'center',
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
      }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
          {t(emptyLabel)}
        </Typography>
      </Box>
    );
  }

  const headerSx = {
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    color: '#94a3b8',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#fafbfc',
    py: 0.85,
    whiteSpace: 'nowrap',
  };

  const cellSx = {
    borderBottom: '1px solid #f1f5f9',
    py: 0.75,
  };

  return (
    <TableContainer sx={{
      background: '#fff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: 'none',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <Table size="small" sx={{ minWidth: showCompany ? 1140 : 980, tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerSx, width: 52, pl: 2.5 }}>#</TableCell>
            <TableCell sx={{ ...headerSx, width: showCompany ? 102 : 140 }}>{t('phase.table.ticker')}</TableCell>
            {showCompany && (
              <TableCell sx={{ ...headerSx, width: 252 }}>{t('phase.table.companyName', 'Tên công ty')}</TableCell>
            )}
            {showSector && (
              <TableCell sx={{ ...headerSx, width: 142 }}>{t('phase.table.sector', 'Ngành')}</TableCell>
            )}
            <TableCell sx={{ ...headerSx, width: showCompany ? 196 : 246 }}>{t('phase.table.phase')}</TableCell>
            <TableCell sx={{ ...headerSx, width: 142 }}>{t('phase.table.confidence')}</TableCell>
            {showScores && (
              <TableCell sx={{ ...headerSx, width: 118, textAlign: 'left' }}>
                <Tooltip
                  title={t('phase.table.wyckoffScoresTooltip')}
                  arrow
                  placement="top"
                >
                  <span>{t('phase.table.wyckoffScores')}</span>
                </Tooltip>
              </TableCell>
            )}
            <TableCell sx={{ ...headerSx, width: 112 }}>{t('phase.table.flags')}</TableCell>
            <TableCell sx={{ ...headerSx, width: 108, pr: 2.5 }}>{t('phase.table.date')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stocks.map((stock, idx) => (
            <TableRow
              key={stock.ticker}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(stock);
                  return;
                }
                navigate(`/stocks/${stock.ticker}`);
              }}
              sx={{
                cursor: 'pointer',
                transition: 'background-color 0.1s',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                },
                '&:last-child td': { borderBottom: 'none' },
              }}
            >
              <TableCell sx={{ ...cellSx, pl: 2.5 }}>
                <RankBadge rank={idx + 1} />
              </TableCell>
              <TableCell sx={cellSx}>
                <Box>
                  <Typography sx={{
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    color: '#4f46e5',
                    fontFamily: 'monospace',
                    letterSpacing: '0.02em',
                    lineHeight: 1.1,
                    '&:hover': { textDecoration: 'underline' },
                  }}>
                    {stock.ticker}
                  </Typography>
                </Box>
              </TableCell>
              {showCompany && (
                <TableCell sx={cellSx}>
                  {stock.company_name ? (
                    <Typography sx={{
                      fontSize: '0.72rem',
                      color: '#64748b',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {stock.company_name}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</Typography>
                  )}
                </TableCell>
              )}
              {showSector && (
                <TableCell sx={cellSx}>
                  <Typography sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: stock.sector_name ? '#64748b' : '#cbd5e1',
                    lineHeight: 1.35,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {stock.sector_name || '—'}
                  </Typography>
                </TableCell>
              )}
              <TableCell sx={cellSx}>
                <Box>
                  <FlatPhaseLabel phase={stock.phase} />
                  <FeatureStrip features={stock.features} />
                </Box>
              </TableCell>
              <TableCell sx={cellSx}>
                <ConfidenceCell value={stock.confidence} confidenceLevel={stock.confidence_level} />
              </TableCell>
              {showScores && (
                <TableCell sx={cellSx}>
                  <ScoreBars scores={stock.scores} phase={stock.phase} />
                </TableCell>
              )}
              <TableCell sx={cellSx}>
                <SignalReadout summary={stock.signal_summary} />
              </TableCell>
              <TableCell sx={{ ...cellSx, pr: 2.5 }}>
                <DateCell tradeDate={stock.trade_date} freshness={stock.freshness} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
